import React, {useState, useMemo, useEffect} from "react";
import {
    Card,
    Button,
    Tag,
    Row,
    Col,
    Typography,
    Space,
    Divider,
    message,
    Select,
    InputNumber,
    DatePicker,
    Form,
    Modal,
    Input,
    Pagination,
    Spin,
    Alert,
} from "antd";
import {
    CalendarOutlined,
    UserOutlined,
    DollarOutlined,
    ToolOutlined,
    SafetyOutlined,
    FilterOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { applyToJobOffer, listMyJobApplications, listPublicJobOffers } from "../../servics/field-work-service";
import { openLoginModal } from "../../store/reducers/authSlice";
import { currentSession, INDIVIDUAL_ROLE } from "../../utils/session";
import { formatAmount, formatDate } from "../../constants/marketplaceCheckout";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;



/** Offre renvoyée par GET /public/job-offers -> forme attendue par l'affichage. */
const toDisplayedOffer = (offer) => ({
    ...offer,
    key: `${offer.tenantCode}:${offer.id}`,
    type: offer.workType,
    authorName: offer.companyName,
});

const AvailableFieldWorks = () => {

    const dispatch = useDispatch();
    // ?offer=<id> : arrivée depuis la recherche, l'offre est ouverte directement.
    const [searchParams] = useSearchParams();
    const requestedOfferId = searchParams.get("offer");
    const activityCountry = useSelector((state) => state.country.activityCountry);
    const countryCode = activityCountry?.value;

    const [offers, setOffers] = useState([]);
    const [currency, setCurrency] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [filters, setFilters] = useState({
        type: null,
        minRate: null,
        startDate: null,
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    // Offres auxquelles le particulier connecté a déjà postulé (bouton désactivé)
    const [appliedOfferIds, setAppliedOfferIds] = useState(() => new Set());
    const [form] = Form.useForm();

    const pageSize = 3;

    // Offres réelles publiées depuis agro-entreprise-page, pour le pays d'activité choisi
    useEffect(() => {
        if (!countryCode) return;
        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        setSelectedOffer(null);
        setCurrentPage(1);
        listPublicJobOffers({ country: countryCode })
            .then((result) => {
                if (cancelled) return;
                setOffers(result.items.map(toDisplayedOffer));
                setCurrency(result.currency);
                setLoading(false);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error(error);
                setOffers([]);
                setLoadError("Impossible de charger les offres pour le moment.");
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [countryCode, reloadKey]);

    useEffect(() => {
        const session = currentSession();
        if (session?.role !== INDIVIDUAL_ROLE) return;
        let cancelled = false;
        listMyJobApplications(session.token)
            .then((applications) => {
                if (!cancelled) setAppliedOfferIds(new Set(applications.map((a) => a.jobOfferId)));
            })
            .catch(() => {
                // simple confort d'affichage : sans cette liste, le backend refuse quand même un doublon (409)
            });
        return () => {
            cancelled = true;
        };
    }, [countryCode]);

    const workTypes = useMemo(
        () => [...new Set(offers.map((offer) => offer.type))].sort((a, b) => a.localeCompare(b, "fr")),
        [offers]
    );

    const updateFilters = (changes) => {
        setFilters((previous) => ({ ...previous, ...changes }));
        setCurrentPage(1);
    };

    // 🔍 Filtrage intelligent
    const filteredOffers = useMemo(() => {
        return offers.filter((offer) => {
            const matchType = !filters.type || offer.type === filters.type;
            const matchRate = !filters.minRate || offer.dailyRate >= filters.minRate;
            const matchDate =
                !filters.startDate ||
                dayjs(offer.startDate).isAfter(dayjs(filters.startDate)) ||
                dayjs(offer.startDate).isSame(dayjs(filters.startDate));

            return matchType && matchRate && matchDate;
        });
    }, [offers, filters]);

    // Pagination locale
    const paginatedOffers = filteredOffers.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    const handleSelectOffer = (offer) => {
        setSelectedOffer(offer);
        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    };

    useEffect(() => {
        if (!requestedOfferId) return;
        const requested = offers.find((offer) => offer.id === requestedOfferId);
        if (requested) handleSelectOffer(requested);
    }, [offers, requestedOfferId]);

    const markApplied = (offerId) =>
        setAppliedOfferIds((previous) => new Set(previous).add(offerId));

    // Connexion vérifiée AVANT d'ouvrir le formulaire : la fenêtre de connexion globale
    // (LoginModal1, montée une seule fois dans App) s'ouvre si personne n'est connecté.
    const handleApplyClick = () => {
        const session = currentSession();
        if (!session) {
            dispatch(openLoginModal());
            return;
        }
        if (session.role !== INDIVIDUAL_ROLE) {
            message.warning("La candidature aux travaux champêtres est réservée aux comptes particuliers.");
            return;
        }
        setIsModalVisible(true);
    };

    const handleApplySubmit = async (values) => {
        const session = currentSession();
        if (!session) {
            setIsModalVisible(false);
            dispatch(openLoginModal());
            return;
        }
        setSubmitting(true);
        try {
            await applyToJobOffer(session.token, selectedOffer.id, {
                availableFrom: values.availability ? values.availability.format("YYYY-MM-DD") : undefined,
                message: values.message || undefined,
                telephone: values.telephone || undefined,
            });
            markApplied(selectedOffer.id);
            message.success("Votre candidature a été envoyée au producteur ✅");
            form.resetFields();
            setIsModalVisible(false);
        } catch (error) {
            const status = error?.response?.status;
            if (status === 409) {
                markApplied(selectedOffer.id);
                setIsModalVisible(false);
                message.info("Vous avez déjà postulé à cette offre.");
            } else if (status === 404) {
                setIsModalVisible(false);
                message.warning("Cette offre n'est plus ouverte aux candidatures.");
                setReloadKey((key) => key + 1);
            } else if (status === 401) {
                setIsModalVisible(false);
                dispatch(openLoginModal());
            } else if (status === 403) {
                message.warning("La candidature aux travaux champêtres est réservée aux comptes particuliers.");
            } else {
                message.error("Votre candidature n'a pas pu être envoyée. Réessayez dans un instant.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const alreadyApplied = selectedOffer ? appliedOfferIds.has(selectedOffer.id) : false;


    return (
        <div className="m-4 p-6 bg-gray-50 min-h-screen">
            <Title level={2} className="text-center mb-6">
                💼 Offres de Travaux Champêtres
            </Title>

            {/* === Barre de filtres === */}
            <Card className="mb-8 shadow-md rounded-lg">
                <Form layout="inline" className="flex flex-wrap justify-center gap-4">
                    <Form.Item label={<><FilterOutlined /> Type</>}>
                        <Select
                            placeholder="Choisir un type"
                            allowClear
                            style={{ width: 180 }}
                            value={filters.type}
                            onChange={(value) => updateFilters({ type: value ?? null })}
                        >
                            {workTypes.map((workType) => (
                                <Option key={workType} value={workType}>{workType}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label={`Taux journalier min.${currency ? ` (${currency})` : ""}`}>
                        <InputNumber
                            min={0}
                            placeholder="ex : 2000"
                            value={filters.minRate}
                            onChange={(value) => updateFilters({ minRate: value })}
                        />
                    </Form.Item>

                    <Form.Item label="À partir du">
                        <DatePicker
                            format="YYYY-MM-DD"
                            value={filters.startDate ? dayjs(filters.startDate) : null}
                            onChange={(date) =>
                                updateFilters({ startDate: date ? date.format("YYYY-MM-DD") : null })
                            }
                        />
                    </Form.Item>

                    <Button type="default" onClick={() => updateFilters({ type: null, minRate: null, startDate: null })}>
                        Réinitialiser
                    </Button>
                </Form>
            </Card>

            {/* === Liste des offres filtrées === */}
            {loadError && (
                <Alert
                    className="mb-6"
                    type="error"
                    showIcon
                    title={loadError}
                    action={<Button size="small" onClick={() => setReloadKey((key) => key + 1)}>Réessayer</Button>}
                />
            )}

            <Spin spinning={loading}>
            <Row gutter={[16, 16]}>
                {paginatedOffers.length > 0 ? (
                    paginatedOffers.map((offer) => (
                        <Col xs={24} sm={12} md={8} key={offer.key}>
                            <Card
                                title={offer.title}
                                bordered
                                className={`shadow-lg rounded-xl hover:shadow-2xl transition-shadow duration-300 cursor-pointer ${
                                    selectedOffer?.key === offer.key ? "border-green-500 border-2" : ""
                                }`}
                                onClick={() => handleSelectOffer(offer)}
                                extra={<Tag color="green">{offer.type}</Tag>}
                            >
                                <Space direction="vertical" style={{ width: "100%" }}>
                                    <Text>
                                        <UserOutlined /> <b>Auteur :</b> {offer.authorName}
                                    </Text>
                                    <Paragraph ellipsis={{ rows: 3 }}>{offer.description}</Paragraph>
                                    <Text>
                                        <CalendarOutlined /> <b>Début :</b> {formatDate(offer.startDate)}
                                    </Text>
                                    <Text>
                                        <DollarOutlined /> <b>Taux journalier :</b> {formatAmount(offer.dailyRate, offer.currency)}
                                    </Text>
                                    <Text type="secondary">
                                        <b>Date limite :</b> {formatDate(offer.applicationDeadline)}
                                    </Text>
                                </Space>
                            </Card>
                        </Col>
                    ))
                ) : (
                    <Col span={24}>
                        <Card className="text-center py-6" bordered={false}>
                            <Text type="secondary">
                                {offers.length === 0
                                    ? `Aucune offre de travaux champêtres n'est ouverte${activityCountry?.label ? ` pour ${activityCountry.label}` : ""} pour le moment.`
                                    : "Aucune offre ne correspond à vos critères de recherche."}
                            </Text>
                        </Card>
                    </Col>
                )}
            </Row>
            </Spin>

            {/* === Pagination === */}
            {filteredOffers.length > pageSize && (
                <div className="flex justify-center mt-8">
                    <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={filteredOffers.length}
                        onChange={(page) => setCurrentPage(page)}
                        showSizeChanger={false}
                    />
                </div>
            )}

            {/* === Section détails de l’offre sélectionnée === */}
            {selectedOffer && (
                <div className="mt-10 p-6 bg-white rounded-xl shadow-md">
                    <Title level={3} className="text-center mb-4 text-green-600">
                        Détails de cette offre champêtre
                    </Title>

                    <Divider />

                    <Row gutter={[24, 16]}>
                        <Col xs={24} md={8}>
                            <Card bordered={false}>
                                <Title level={4}>{selectedOffer.title}</Title>
                                <Tag color="blue">{selectedOffer.type}</Tag>
                                <Text type="secondary">
                                    Publiée par <b>{selectedOffer.authorName}</b>
                                </Text>
                                <Paragraph className="mt-3">
                                    <CalendarOutlined /> <b>Du :</b> {formatDate(selectedOffer.startDate)} <br />
                                    <CalendarOutlined /> <b>Au :</b> {formatDate(selectedOffer.endDate)} <br />
                                    <DollarOutlined /> <b>Taux journalier :</b> {formatAmount(selectedOffer.dailyRate, selectedOffer.currency)}
                                </Paragraph>
                                <Tag color="green">Candidature jusqu&apos;au {formatDate(selectedOffer.applicationDeadline)}</Tag>
                            </Card>
                        </Col>

                        <Col xs={24} md={16}>
                            <Card bordered={false}>
                                <Paragraph>
                                    <b>Descriptif du travail :</b> <br />
                                    {selectedOffer.description}
                                </Paragraph>
                                <Divider dashed />
                                <Paragraph>
                                    <ToolOutlined /> <b>Prérequis :</b> <br />
                                    {selectedOffer.prerequisites || "Aucun"}
                                </Paragraph>
                                <Paragraph>
                                    <SafetyOutlined /> <b>Précautions :</b> <br />
                                    {selectedOffer.precautions || "Aucune"}
                                </Paragraph>

                                <Divider />
                                <Button type="primary" size="large" onClick={handleApplyClick} disabled={alreadyApplied}>
                                    {alreadyApplied ? "Candidature envoyée ✓" : "Postuler maintenant"}
                                </Button>
                            </Card>
                        </Col>
                    </Row>
                </div>
            )}

            {/* === Modal de candidature (particulier connecté uniquement) === */}
            <Modal
                title="🧾 Formulaire de candidature"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
                destroyOnClose
            >
                <Paragraph type="secondary">
                    Vos nom, email et téléphone sont ceux de votre compte ASSIGANTO.
                </Paragraph>
                <Form form={form} layout="vertical" onFinish={handleApplySubmit}>
                    <Form.Item
                        label="Date de disponibilité"
                        name="availability"
                        rules={[{ required: true, message: "Indiquez votre disponibilité" }]}
                    >
                        <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item label="Téléphone pour cette offre (si différent de votre compte)" name="telephone">
                        <Input placeholder="Ex : +228 90 00 00 00" maxLength={50} />
                    </Form.Item>
                    <Form.Item label="Message (optionnel)" name="message">
                        <Input.TextArea rows={3} maxLength={2000} placeholder="Ajoutez un message au producteur..." />
                    </Form.Item>
                    <div className="text-right">
                        <Button onClick={() => setIsModalVisible(false)} style={{ marginRight: 8 }}>
                            Annuler
                        </Button>
                        <Button type="primary" htmlType="submit" loading={submitting}>
                            Envoyer la candidature
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default AvailableFieldWorks;
